import { useCallback, useState } from 'react';
import type { IsGroupCollapsed } from './nested-workflow-tree-to-flow';

export type NestedTreeCollapse = {
  /** Passed to the layout pass; see {@link IsGroupCollapsed}. */
  readonly isCollapsed: IsGroupCollapsed;
  /** Fold or unfold one group. Callers pass the state they want, not a flip. */
  readonly setCollapsed: (key: string, collapsed: boolean) => void;
};

/**
 * Which groups in the tree are folded to a header.
 *
 * Every workflow is expanded by default — the whole point of the hierarchy is to
 * see the nesting, so a box that folded itself the moment it appeared read as the
 * view hiding part of the answer. Only an explicit toggle folds a box, and this
 * stores just those toggles: the tree is not all there on first render (levels
 * resolve one fetch at a time), so a level that arrives later still comes in
 * expanded, and a box a user folded stays folded.
 */
export function useNestedTreeCollapse(): NestedTreeCollapse {
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(
    new Map()
  );

  const isCollapsed = useCallback<IsGroupCollapsed>(
    (key) => overrides.get(key) ?? false,
    [overrides]
  );

  const setCollapsed = useCallback((key: string, collapsed: boolean) => {
    setOverrides((previous) => new Map(previous).set(key, collapsed));
  }, []);

  return { isCollapsed, setCollapsed };
}
